import { Link } from "@tanstack/react-router";
import { ArrowRight, MapPin, CheckCircle2 } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import type { ServiceArea } from "@/lib/serviceAreaWebDesign";
import { buildLocalBusinessSchema, SCHEMA_LOCALBUSINESS_ID } from "@/lib/entitySchema";

const SITE = "https://coloradowebimpressions.com";

export function serviceAreaHead(a: ServiceArea) {
  const url = SITE + a.path;
  return {
    meta: [
      { title: a.title },
      { name: "description", content: a.description },
      { property: "og:title", content: a.title },
      { property: "og:description", content: a.description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildLocalBusinessSchema([a.name, ...a.towns])),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: `Web Design in ${a.name}`,
          serviceType: "Web Design",
          url,
          provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
          areaServed: a.towns.map((t) => ({ "@type": "City", name: `${t}, CO` })),
          offers: { "@type": "Offer", price: "1850", priceCurrency: "USD", url: `${SITE}/pricing` },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
            { "@type": "ListItem", position: 2, name: "Colorado Web Design", item: SITE + "/colorado-web-design" },
            { "@type": "ListItem", position: 3, name: `Web Design ${a.name}`, item: url },
          ],
        }),
      },
    ],
  };
}

export function ServiceAreaWebDesignPage({ area: a }: { area: ServiceArea }) {
  return (
    <SiteLayout>
      <PageHero
        eyebrow={`Web Design · ${a.name.replace(/^the /, "")}`}
        title={<>{a.heroLead} <span className="text-gradient-gold">customers</span></>}
        description={a.intro}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Link to="/free-mockup" className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-6 py-3.5 font-bold text-brand-black">
            Get a Free Mockup <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/colorado-web-design" className="inline-flex items-center justify-center gap-2 rounded-md border border-brand-cream/30 px-6 py-3.5 font-semibold text-brand-cream">
            Colorado Web Design
          </Link>
        </div>
      </PageHero>

      <PricingTeaserStrip />

      <section className="bg-background py-14 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="text-balance text-3xl font-bold text-foreground lg:text-4xl">What {a.name} businesses need from a website</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {a.local.map((h) => (
              <div key={h.title} className="rounded-xl border border-border bg-card p-6">
                <CheckCircle2 className="h-6 w-6 text-brand-gold-deep" />
                <h3 className="mt-3 text-lg font-bold text-foreground">{h.title}</h3>
                <p className="mt-2 text-foreground/75">{h.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted py-14">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground">Towns we serve near {a.name}</h2>
          <ul className="mt-5 flex flex-wrap gap-2">
            {a.towns.map((t) => (
              <li key={t} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-sm text-foreground">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-brand-gold-deep" /> {t}
              </li>
            ))}
          </ul>
          <p className="mt-5 text-foreground/75">
            One office in Colorado Springs, serving businesses statewide. See our full{" "}
            <Link to="/colorado-web-design" className="font-semibold text-brand-gold-deep underline">Colorado web design</Link> services and{" "}
            <Link to="/pricing" hash="website-pricing" className="font-semibold text-brand-gold-deep underline">website packages</Link>.
          </p>
        </div>
      </section>

      <section className="bg-background py-14">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground">Questions about web design in {a.name}</h2>
          <div className="mt-6 space-y-4">
            {a.faqs.map((f) => (
              <div key={f.q} className="rounded-lg border border-border p-5">
                <h3 className="font-bold text-foreground">{f.q}</h3>
                <p className="mt-2 text-foreground/75">{f.a}</p>
              </div>
            ))}
          </div>
          <h2 className="mt-12 text-xl font-bold text-foreground">Related pages</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            {a.nearby.map((n) => (
              <Link key={n.to} to={n.to} className="inline-flex items-center gap-1 rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted">
                {n.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
